// ---------- Utilities ----------
export const DEFAULT_EPOCHS = 20
export const DEFAULT_BATCH_SIZE = 16

export const randomNumberGenerator = () =>
  Math.floor(Math.random() * 9e14) + 1e14

const randomColor = () =>
  '#' + Math.floor(Math.random() * 16777215).toString(16)

export const createDefaultAudioClasses = () => ({
  Background_Noise: {
    id: randomNumberGenerator(),
    className: 'Background_Noise',
    sampleType: 'Audio',
    capturedAudio: [],
    capturedAudioCount: 0,
    bgColor: randomColor(),
  },
  Class_1: {
    id: randomNumberGenerator(),
    className: 'Class_1',
    sampleType: 'Audio',
    capturedAudio: [],
    capturedAudioCount: 0,
    bgColor: randomColor(),
    categoryName: 'Undefined',
    CategoryName: 'Undefined',
  },
})

const modelId = Date.now()

const defaultAppModeStorage = {
  chooseDevice: {
    cam: false,
    mic: false,
    pc: false,
  },
  trainModeSelect: { cam: false, mic: false },
  trainMachine: {
    currentModelId: modelId,
    camModels: [
      {
        modelId,
        modelName: 'Model 1',
        epochs: DEFAULT_EPOCHS,
        batchSize: DEFAULT_BATCH_SIZE,
        detectionType: 'cnn',
        ImageClasses: {
          Class_1: {
            id: Date.now(),
            className: 'Class 1',
            sampleType: 'Image',
            indexedDbId: 'Class 1',
            capturedImages: [],
            capturedPoses: [],
            bgColor: randomColor(),
          },
        },
        roiRegions: [],
        AudioClasses: createDefaultAudioClasses(),
        isGenerateTensor: false,
      },
    ],
    advanceSetting: {
      epochs: DEFAULT_EPOCHS,
      batchSize: DEFAULT_BATCH_SIZE,
    },
  },
}

// ---------- Load from Session ----------
const loadInitialState = () => {
  try {
    const raw = sessionStorage.getItem('appModeStorage') || localStorage.getItem('appModeStorage')
    const saved = JSON.parse(raw)
    if (!saved?.trainMachine) return defaultAppModeStorage

    saved.trainMachine.camModels.forEach((model) => {
      // Ensure AudioClasses has required defaults
      const audioClasses = model.AudioClasses || {}
      const classList = Object.values(audioClasses)
      const hasBackgroundNoise = classList.some((cls) => cls.className === 'Background_Noise')
      const hasOtherClasses = classList.some((cls) => cls.className !== 'Background_Noise')
      const defaults = createDefaultAudioClasses()
      if (!hasBackgroundNoise) audioClasses.Background_Noise = defaults.Background_Noise
      if (!hasOtherClasses) audioClasses.Class_1 = defaults.Class_1
      // Backfill sampleType on audio classes saved before this field was added
      Object.values(audioClasses).forEach((cls) => {
        if (!cls.sampleType) cls.sampleType = 'Audio'
      })
      model.AudioClasses = audioClasses

      // Ensure pose fields exist
      if (!model.detectionType) model.detectionType = 'cnn'
      if (!model.roiRegions) model.roiRegions = []
      Object.values(model.ImageClasses || {}).forEach((cls) => {
        if (!cls.capturedPoses) cls.capturedPoses = []
      })
    })

    return saved
  } catch {
    return defaultAppModeStorage
  }
}

const saveState = (state) => {
  const serialized = JSON.stringify(state)
  sessionStorage.setItem('appModeStorage', serialized)
  localStorage.setItem('appModeStorage', serialized)
}

// ---------- Reducer ----------
const appModeStorage_Reducer = (state = loadInitialState(), action) => {
  let newState = state

  switch (action.type) {
    case 'CHOOSE_DEVICE':
      newState = { ...state, chooseDevice: action.payload }
      break

    case 'TRAIN_MODE_SELECT':
      newState = { ...state, trainModeSelect: action.payload }
      break

    case 'SET_TRAIN_MACHINE_STORE':
      newState = { ...state, trainMachine: action.payload }
      break

    // Re-read the whole slice from storage (used after importing a project so
    // the trained models appear without a full page reload).
    case 'HYDRATE_APP_MODE_STORAGE':
      newState = loadInitialState()
      break

    case 'ADD_CLASSES': {
      const { classType, key, data } = action.payload
      const camModels = state.trainMachine.camModels.map((model) =>
        model.modelId === state.trainMachine.currentModelId
          ? {
              ...model,
              [classType]: {
                ...model[classType],
                [key]: data,
              },
              isGenerateTensor: false,
            }
          : model
      )
      newState = { ...state, trainMachine: { ...state.trainMachine, camModels } }
      break
    }

    case 'CHANGE_CLASSES': {
      const { classType, data } = action.payload
      const camModels = state.trainMachine.camModels.map((model) =>
        model.modelId === state.trainMachine.currentModelId
          ? { ...model, [classType]: data }
          : model
      )
      newState = { ...state, trainMachine: { ...state.trainMachine, camModels } }
      break
    }

    case 'ADVANCE_SETTING': {
      const { epochs, batchSize } = action.payload
      const camModels = state.trainMachine.camModels.map((model) =>
        model.modelId === state.trainMachine.currentModelId
          ? { ...model, epochs, batchSize }
          : model
      )
      newState = { ...state, trainMachine: { ...state.trainMachine, camModels } }
      break
    }

    case 'TOGGLE_NEXT_BTN': {
      const camModels = state.trainMachine.camModels.map((model) =>
        model.modelId === state.trainMachine.currentModelId
          ? { ...model, isGenerateTensor: action.payload }
          : model
      )
      newState = { ...state, trainMachine: { ...state.trainMachine, camModels } }
      break
    }

    case 'SET_CURRENT_MODEL_ID':
      newState = {
        ...state,
        trainMachine: { ...state.trainMachine, currentModelId: action.payload },
      }
      break

    case 'SET_MODEL_DETAILS':
      newState = {
        ...state,
        trainMachine: { ...state.trainMachine, camModels: action.payload },
      }
      break

    case 'ADD_POSE_SAMPLE': {
      const { classKey, poseVector } = action.payload
      const data = structuredClone(state)
      const currentModel = data.trainMachine.camModels.find(
        (model) => model.modelId === data.trainMachine.currentModelId
      )
      if (!currentModel) return state
      const selectedClass = currentModel.ImageClasses[classKey]
      if (!selectedClass) return state
      selectedClass.capturedPoses = [...(selectedClass.capturedPoses || []), poseVector]
      selectedClass.capturedImages = [...(selectedClass.capturedImages || []), poseVector.snapshot]
      currentModel.isGenerateTensor = false
      newState = data
      break
    }

    case 'SET_DEFAULT_APPMODESTORAGE':
      newState = structuredClone(defaultAppModeStorage)
      break

    default:
      return state
  }

  saveState(newState)
  return newState
}

export default appModeStorage_Reducer
